import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";

export default function NeonSpeedometer({ value }) {
  const [displayValue, setDisplayValue] = useState(0);
  const [dateTime, setDateTime] = useState("");

  const [isBouncingGauge, setIsBouncingGauge] = useState(false);
  const [isBouncingTime, setIsBouncingTime] = useState(false);

  // ---- Date & Time Updater ----
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        dateStyle: "medium",
        timeStyle: "medium",
      });
      setDateTime(now);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // ---- Smooth Gauge Number Animation ----
  useEffect(() => {
    let frame = 0;
    const totalFrames = 40;

    const start = displayValue;
    const end = value;
    const diff = end - start;

    const animate = () => {
      frame++;
      const progress = frame / totalFrames;
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + diff * eased;
      setDisplayValue(Math.round(current));
      if (frame < totalFrames) requestAnimationFrame(animate);
    };

    animate();
  }, [value]);

  const percent = Math.min(100, (displayValue / 100) * 100);
  const angle = (percent / 100) * 180 - 90;

  // ---- Click Bounce Handlers ----
  const bounceGauge = () => {
    setIsBouncingGauge(true);
    setTimeout(() => setIsBouncingGauge(false), 250);
  };

  const bounceTime = () => {
    setIsBouncingTime(true);
    setTimeout(() => setIsBouncingTime(false), 250);
  };

  return (
    <Box sx={{ textAlign: "center", mt: 5 }}>

      {/* -------------------- DATE TIME WITH HOVER + CLICK BOUNCE -------------------- */}
      <Typography
        onClick={bounceTime}
        sx={{
          color: "#1E90FF",
          fontSize: "clamp(12px, 1.7vw, 18px)",
          letterSpacing: "4px",
          fontFamily: "'Orbitron', sans-serif",
          textShadow: `
            0 0 6px #1E90FF,
            0 0 15px rgba(30,144,255,0.7),
            0 0 25px rgba(30,144,255,0.4)
          `,
          background: "rgba(0, 20, 40, 0.45)",
          padding: "6px 16px",
          borderRadius: "10px",
          border: "1px solid rgba(30,144,255,0.3)",
          backdropFilter: "blur(6px)",
          display: "inline-block",
          cursor: "pointer",

          transform: isBouncingTime
            ? "scale(1.12)"
            : "scale(1)",
          transition: isBouncingTime
            ? "transform 0.15s cubic-bezier(.26,.53,.74,1.48)"
            : "transform 0.25s ease-out",

          "&:hover": {
            transform: "scale(1.07)",
            transition: "transform 0.25s ease-out",
          },
        }}
      >
        {dateTime}
      </Typography>

      {/* -------------------- SPEEDOMETER CONTAINER -------------------- */}
      <Box
        onClick={bounceGauge}
        sx={{
          width: 300,
          height: 180,
          mx: "auto",
          mt: 3,
          position: "relative",
          cursor: "pointer",

          transform: isBouncingGauge
            ? "scale(1.1)"
            : "scale(1)",
          transition: isBouncingGauge
            ? "transform 0.15s cubic-bezier(.26,.53,.74,1.48)"
            : "transform 0.3s ease-out",

          "&:hover": {
            transform: "scale(1.05)",
            transition: "transform 0.25s ease-out",
          },

          background: "radial-gradient(circle at 50% 120%, #000 40%, #0a0a0a 90%)",
          borderRadius: "160px 160px 14px 14px",
          border: "2px solid rgba(30,144,255,0.25)",
          boxShadow: "0 0 40px rgba(30,144,255,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Pulse Glow */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            animation: "pulse 3s infinite",
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 25px rgba(30,144,255,0.3)" },
              "50%": { boxShadow: "0 0 40px rgba(30,144,255,0.6)" },
              "100%": { boxShadow: "0 0 25px rgba(30,144,255,0.3)" },
            },
          }}
        />

        {/* Fill Arc */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            borderBottom: "none",
            background: `conic-gradient(#1E90FF ${percent}%, transparent ${percent}%)`,
            filter: "drop-shadow(0 0 12px #1E90FF)",
            transition: "background 0.4s ease-out",
          }}
        />

        {/* Needle */}
        <Box
          sx={{
            position: "absolute",
            bottom: 10,
            left: "50%",
            width: 6,
            height: 80,
            background: "#1E90FF",
            transformOrigin: "bottom center",
            transform: `translateX(-50%) rotate(${angle}deg)`,
            borderRadius: 3,
            boxShadow: "0 0 10px #1E90FF",
            transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />

        {/* Needle center cap */}
        <Box
          sx={{
            position: "absolute",
            bottom: 6,
            left: "50%",
            width: 16,
            height: 16,
            background: "#1E90FF",
            borderRadius: "50%",
            transform: "translateX(-50%)",
            boxShadow: "0 0 12px #1E90FF",
          }}
        />

        {/* Value Number */}
        <Typography
          variant="h4"
          sx={{
            position: "absolute",
            bottom: 58,
            left: "50%",
            transform: "translateX(-50%)",
            color: "#1E90FF",
            fontWeight: 700,
            letterSpacing: 2,
            textShadow: "0 0 10px #1E90FF",
          }}
        >
          {displayValue}
        </Typography>

        {/* Label */}
        <Typography
          variant="body2"
          sx={{
            position: "absolute",
            bottom: 18,
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(30,144,255,0.7)",
            letterSpacing: 1,
          }}
        >
          Real-Time Sessions
        </Typography>
      </Box>
    </Box>
  );
}
